// page-backlog-review-gestures.ts — Les gestes d'arbitrage sur une
// recommandation : appliquer, rejeter, reporter, reprendre, annoter, marquer.
//
// Extrait de `page-backlog-review.ts` le 2026-09-13. Chaque geste demande ce
// qu'il lui faut (motif, échéance, note) dans une modale, puis ÉCRIT par un
// seul chemin, `patchReco`, qui porte le `row_version` et recharge l'écran.
// L'écran fournit le contexte : la revue AFFICHÉE, lue au moment du geste, et
// son rechargement — jamais capturés, parce qu'une modale se confirme plus
// tard et que l'utilisateur a pu naviguer entre-temps.
//
// La vue Tableau appelle exactement les mêmes gestes (`boardHandlers`) : un
// dépôt de carte n'est jamais une écriture, il ouvre la même modale.
//
// Globaux ambiants : esc, t, api, toast, openModal, closeModal.

import type { BoardHandlers, BoardReco } from './page-backlog-review-board.js';
import { businessScreen, profileBadges, vitalitiesOf } from './page-backlog-review-list.js';
import { label, type Reco, type Review } from './page-backlog-review-types.js';

export interface GestureContext {
  /** La revue affichée, lue à chaque geste — jamais capturée. */
  review(): Review | null;
  reload(): Promise<void>;
}

export async function patchReco(
  ctx: GestureContext,
  rid: number,
  payload: Record<string, unknown>,
): Promise<void> {
  const review = ctx.review();
  const reco = review?.recommendations.find((r) => r.id === rid);
  if (!review || !reco) return;
  try {
    await api.patchBacklogRecommendation(review.id, rid, {
      ...payload,
      row_version: reco.row_version,
    });
    await ctx.reload();
  } catch (e: unknown) {
    toast(e instanceof Error ? e.message : t('common.error'), 'error');
  }
}

/** Ouvre l'écran métier, puis attend la confirmation que l'écriture a eu lieu.
 *
 * L'écran s'ouvre dans un NOUVEL onglet : la revue garde ainsi sa position
 * exacte, ses filtres et son défilement — revenir sur une liste réinitialisée
 * après chaque application rendrait l'arbitrage pénible. Le marquage reste un
 * second geste explicite : on ne marque jamais appliqué ce qui ne l'a pas été.
 */
function _openBusinessScreen(ctx: GestureContext, reco: Reco): void {
  const target = businessScreen(reco);
  if (!target) return;
  window.open(target, '_blank', 'noopener');
  openModal(
    t('review.apply.title'),
    `<p>${esc(t('review.apply.opened'))}</p><p>${esc(t('review.apply.confirmHint'))}</p>`,
    async () => {
      closeModal();
      await patchReco(ctx, reco.id, { pile: 'applied' });
    },
    t('review.apply.done'),
  );
}

/** Garde-fou : une seule friction, au seul endroit où une décision devient
 * irréversible. « Appliquer quand même » reste toujours disponible — le PMO
 * peut avoir de bonnes raisons de maintenir sa position. */
export function confirmApply(ctx: GestureContext, reco: Reco): void {
  if (!reco.changed_since_analysis) {
    _openBusinessScreen(ctx, reco);
    return;
  }
  const rows = (reco.changes || [])
    .map(
      (c) =>
        `<tr><td>${esc(c.label)}</td><td>${esc(String(c.before ?? '—'))}</td><td>→</td><td>${esc(String(c.after ?? '—'))}</td></tr>`,
    )
    .join('');
  openModal(
    t('review.guard.title'),
    `<p>${esc(t('review.guard.intro'))}</p><table>${rows}</table><p>${esc(t('review.guard.check'))}</p>`,
    () => {
      closeModal();
      _openBusinessScreen(ctx, reco);
    },
    t('review.guard.applyAnyway'),
  );
}

export function askReject(ctx: GestureContext, reco: Reco): void {
  openModal(
    t('review.reject.title'),
    `<div class="form-group">
      <label for="reject-reason">${esc(t('review.reject.reason'))}</label>
      <select id="reject-reason">
        <option value="false_positive">${esc(t('review.reject.falsePositive'))}</option>
        <option value="refused">${esc(t('review.reject.refused'))}</option>
      </select>
    </div>`,
    async () => {
      const reason =
        (document.getElementById('reject-reason') as HTMLSelectElement | null)?.value ||
        'false_positive';
      closeModal();
      await patchReco(ctx, reco.id, { pile: 'rejected', reject_reason: reason });
    },
    t('review.reject.confirm'),
  );
}

export function askSnooze(ctx: GestureContext, reco: Reco): void {
  const dflt = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
  openModal(
    t('review.snooze.title'),
    `<div class="form-group">
      <label for="snooze-until">${esc(t('review.snooze.until'))}</label>
      <input type="date" id="snooze-until" value="${dflt}">
    </div>`,
    async () => {
      const until = (document.getElementById('snooze-until') as HTMLInputElement | null)?.value;
      if (!until) {
        toast(t('review.snooze.required'), 'error');
        return;
      }
      closeModal();
      await patchReco(ctx, reco.id, { pile: 'snoozed', snooze_until: until });
    },
    t('review.snooze.confirm'),
  );
}

export function askAnnotation(ctx: GestureContext, reco: Reco): void {
  // Le bouton de suppression n'existe QUE s'il y a une note à supprimer : le
  // proposer sur une annotation vide promettrait un geste sans effet.
  const removeBtn = reco.annotation
    ? `<button type="button" class="btn btn-secondary btn-sm" id="btn-annotation-remove"
        style="margin-top:8px">${esc(t('review.annotation.remove'))}</button>`
    : '';
  openModal(
    t('review.annotate'),
    `<div class="form-group">
      <label for="annotation">${esc(t('review.annotation.label'))}</label>
      <textarea id="annotation" rows="3">${esc(reco.annotation || '')}</textarea>
      <div class="text-xs text-muted">${esc(t('review.annotation.hint'))}</div>
      ${removeBtn}
    </div>`,
    async () => {
      const value = (document.getElementById('annotation') as HTMLTextAreaElement | null)?.value;
      closeModal();
      await patchReco(ctx, reco.id, { annotation: value || '' });
    },
  );
  document.getElementById('btn-annotation-remove')?.addEventListener('click', () => {
    void (async () => {
      closeModal();
      await patchReco(ctx, reco.id, { annotation: '' });
    })();
  });
}

/** Le geste demandé par un bouton `data-act` de la vue Liste, ou par un dépôt
 *  de carte de la vue Tableau : les deux passent par ici. */
export function requestPile(ctx: GestureContext, reco: Reco, pile: string): void {
  if (pile === 'applied') confirmApply(ctx, reco);
  else if (pile === 'rejected') askReject(ctx, reco);
  else if (pile === 'snoozed') askSnooze(ctx, reco);
  else void patchReco(ctx, reco.id, { pile: 'todo' });
}

export function boardHandlers(ctx: GestureContext): BoardHandlers {
  return {
    requestPile: (reco: BoardReco, pile: string): void => {
      const full = ctx.review()?.recommendations.find((r) => r.id === reco.id);
      if (full) requestPile(ctx, full, pile);
    },
    setFlag: (reco: BoardReco, value: boolean): void => {
      void patchReco(ctx, reco.id, { to_arbitrate: value });
    },
    annotate: (reco: BoardReco): void => {
      const full = ctx.review()?.recommendations.find((r) => r.id === reco.id);
      if (full) askAnnotation(ctx, full);
    },
    familyLabel: (family: string): string => label('review.family', family),
    pileLabel: (pile: string): string => label('review.pile', pile),
    profileBadges: (reco: BoardReco): string => profileBadges(vitalitiesOf(ctx.review(), reco)),
    ruleLabel: (reco: BoardReco): string =>
      reco.provenance
        ? `${label('review.provenance', reco.provenance)} · ${label('review.confidence', reco.confidence || '')}`
        : '',
  };
}
